import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { FR, GB } from 'country-flag-icons/react/3x2';
import { useLanguage } from '../src/context/LanguageContext';

interface HeaderProps {
  onDownloadClick: () => void;
  /** Conservé pour compatibilité avec les pages existantes (non utilisé dans la barre). */
  onMySpaceClick?: () => void;
  /** Barre fixe translucide au-dessus du hero (page d'accueil). */
  variant?: 'default' | 'overlay';
}

const PAYLINK_MEGA_ITEMS = [
  {
    title: 'PayLink App',
    subtitle: 'Global Remittance App',
    description:
      "Transferts internationaux fluides et transparents: Gardez le lien avec vos proches, où qu'ils soient.",
    imageSrc: '/assets/PayLinkMode.gif',
    href: 'https://paylink.cool',
  },
  {
    title: 'PayLink Business',
    subtitle: 'Plateform & Commerce APIs',
    description:
      'Encaissez, scalez et intégrez PayLink : checkout, parcours clients et APIs pensés pour les entreprises.',
    imageSrc: '/assets/illustrationcheckout1.png',
    href: 'https://paylink.business',
  },
] as const;

const Header: React.FC<HeaderProps> = ({ onDownloadClick, variant = 'default' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPayLinkOpen, setIsPayLinkOpen] = useState(false);
  const payLinkRootRef = useRef<HTMLDivElement>(null);
  const isOverlay = variant === 'overlay';
  const { language, toggleLanguage, t } = useLanguage();
  const isEnglish = language === 'en';
  const CurrentFlag = isEnglish ? GB : FR;
  const payLinkLabel = isEnglish ? 'Discover PayLink' : 'Découvrir PayLink';
  const payLinkMenuLabel = isEnglish ? 'PayLink ecosystem' : 'Écosystème PayLink';
  const visitSiteLabel = isEnglish ? 'Visit site →' : 'Visiter le site →';
  const downloadLabel = isEnglish ? 'Download' : 'Télécharger';
  const openLabel = isEnglish ? 'Open' : 'Ouvrir';

  const toggleMenu = () => {
    setIsMenuOpen((open) => {
      const next = !open;
      if (next) setIsPayLinkOpen(false);
      return next;
    });
  };

  useEffect(() => {
    if (!isPayLinkOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPayLinkOpen(false);
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const root = payLinkRootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setIsPayLinkOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isPayLinkOpen]);

  return (
    <header
      className={
        isOverlay
          ? 'fixed top-0 left-0 right-0 z-40 border-b border-black/[0.06] bg-white/75 backdrop-blur-md'
          : 'sticky top-0 z-50 border-b backdrop-blur-md bg-white/95 border-white/20'
      }
    >
      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center gap-3 min-h-[5.25rem] py-2 md:min-h-[5.5rem]">
          <div className="flex gap-2 items-center min-w-0 sm:gap-3">
            <a
              href="/"
              className="flex items-center shrink-0 group xl:-ml-2 2xl:-ml-4"
            >
              <img
                src="/assets/BurokGradient.png"
                alt="Burok"
                className="object-contain w-auto h-12 transition-transform duration-300 sm:h-14 md:h-16 group-hover:scale-105"
              />
            </a>

            <div className="relative min-w-0" ref={payLinkRootRef}>
              <button
                type="button"
                id="paylink-nav-trigger"
                aria-haspopup="true"
                aria-controls="paylink-mega-menu"
                onClick={() => {
                  setIsPayLinkOpen((o) => !o);
                  setIsMenuOpen(false);
                }}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200/90 bg-white/90 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:border-[#fc2207]/40 hover:text-[#fc2207] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#fc2207] focus-visible:ring-offset-2 sm:px-4 sm:py-2.5"
              >
                <span className="truncate">{payLinkLabel}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 opacity-70 transition-transform ${isPayLinkOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {isPayLinkOpen && (
                <>
                  <div
                    className="fixed inset-x-0 bottom-0 top-[4.65rem] z-[48] bg-black/25 backdrop-blur-[2px] sm:top-[5.15rem] md:top-[5.35rem]"
                    aria-hidden
                    onClick={() => setIsPayLinkOpen(false)}
                  />
                  <div
                    id="paylink-mega-menu"
                    role="menu"
                    aria-labelledby="paylink-nav-trigger"
                    className="fixed left-1/2 top-[4.65rem] z-[60] w-[min(calc(100vw-1.5rem),62rem)] max-w-[calc(100vw-1.5rem)] -translate-x-1/2 sm:top-[5.15rem] md:top-[5.35rem]"
                  >
                    <div className="max-h-[min(82vh,34rem)] overflow-y-auto overscroll-contain rounded-2xl border border-gray-200/90 bg-white p-4 shadow-2xl sm:max-h-[min(85vh,36rem)] sm:p-6 md:max-h-none md:overflow-visible">
                      <p className="mb-4 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        {payLinkMenuLabel}
                      </p>
                      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
                        {PAYLINK_MEGA_ITEMS.map((item) => (
                          <a
                            key={item.href}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="menuitem"
                            onClick={() => setIsPayLinkOpen(false)}
                            className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-gray-50/50 transition hover:border-[#fc2207]/35 hover:bg-white hover:shadow-lg"
                          >
                            <div className="relative aspect-[16/10] w-full overflow-hidden bg-gray-100">
                              <img
                                src={item.imageSrc}
                                alt={item.title}
                                className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                              />
                            </div>
                            <div className="flex flex-1 flex-col gap-1.5 p-4">
                              <span className="text-base font-bold text-gray-900 group-hover:text-[#fc2207]">
                                {item.title}
                              </span>
                              <span className="text-xs font-semibold tracking-wide text-gray-700">
                                {item.subtitle}
                              </span>
                              <span className="text-sm leading-relaxed text-gray-600">
                                {item.description}
                              </span>
                              <span className="mt-2 text-xs font-medium text-[#fc2207]">
                                {visitSiteLabel}
                              </span>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hidden shrink-0 items-center space-x-4 md:flex">
            <button
              type="button"
              onClick={() => {
                toggleLanguage();
              }}
              className="inline-flex items-center rounded-full border border-gray-200/90 bg-white/90 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:border-[#fc2207]/40 hover:text-[#fc2207] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#fc2207] focus-visible:ring-offset-2"
              aria-label={t({ fr: 'Passer en anglais', en: 'Switch to French' })}
              title={isEnglish ? 'English' : 'Français'}
            >
              <span className="inline-flex h-6 w-9 items-center justify-center overflow-hidden rounded-md ring-1 ring-black/10">
                <CurrentFlag className="h-full w-full" aria-hidden />
              </span>
              <span className="sr-only">{isEnglish ? 'English' : 'Français'}</span>
            </button>
            <button
              type="button"
              onClick={onDownloadClick}
              className="group relative overflow-hidden rounded-full border-2 border-[#fc2207] px-6 py-3 font-semibold text-[#fc2207] transition-all duration-300 hover:scale-105 hover:text-white hover:shadow-lg"
            >
              <span className="relative z-10 flex items-center gap-2">
                <img
                  src="/assets/BUROKORANGELOGOSMALLER.png"
                  alt=""
                  className="h-5 w-auto object-contain sm:h-6"
                  aria-hidden
                />
                {downloadLabel}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#fc2207] to-[#fe5801] opacity-0 transition-all duration-300 group-hover:opacity-100" />
            </button>

            <a
              href="https://paylink.cool"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center overflow-hidden rounded-full bg-gradient-to-r from-[#fc2207] to-[#fe5801] px-6 py-3 font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-xl sm:px-8"
            >
              <span className="relative z-10 flex items-center gap-0.5">
                <span>{openLabel}</span>
                <img
                  src="/assets/PayLink2.png"
                  alt=""
                  className="h-5 w-auto object-contain drop-shadow-sm sm:h-6"
                  aria-hidden
                />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#ff4a66] to-[#ff8400] opacity-0 transition-all duration-300 group-hover:opacity-100" />
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden shrink-0">
            <button
              type="button"
              onClick={toggleMenu}
              className="text-gray-700 hover:text-[#fc2207] transition-all duration-300"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 border-t backdrop-blur-md bg-white/95 border-white/20">
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    toggleLanguage();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full rounded-full border border-gray-200/90 bg-white px-6 py-3 text-center font-semibold text-gray-800 transition-all duration-300 hover:text-[#fc2207]"
                  aria-label={t({ fr: 'Passer en anglais', en: 'Switch to French' })}
                >
                  <span className="mx-auto inline-flex h-7 w-11 items-center justify-center overflow-hidden rounded-md ring-1 ring-black/10">
                    <CurrentFlag className="h-full w-full" aria-hidden />
                  </span>
                  <span className="sr-only">{isEnglish ? 'English' : 'Français'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDownloadClick();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full rounded-full border-2 border-[#fc2207] px-6 py-3 text-center font-semibold text-[#fc2207] transition-all duration-300 hover:bg-[#fc2207] hover:text-white"
                >
                  <span className="flex items-center justify-center gap-2">
                    <img
                      src="/assets/BUROKORANGELOGOSMALLER.png"
                      alt=""
                      className="h-5 w-auto object-contain"
                      aria-hidden
                    />
                    {downloadLabel}
                  </span>
                </button>
                <a
                  href="https://paylink.cool"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full rounded-full bg-gradient-to-r from-[#fc2207] to-[#fe5801] px-6 py-3 text-center font-semibold text-white transition-all duration-300 hover:shadow-xl"
                >
                  <span className="flex items-center justify-center gap-0.5">
                    <span>{openLabel}</span>
                    <img
                      src="/assets/PayLink2.png"
                      alt=""
                      className="h-5 w-auto object-contain drop-shadow-sm"
                      aria-hidden
                    />
                  </span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
